import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRightIcon, DownloadIcon, SearchIcon, UserPlusIcon, UsersIcon } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../ui/PageHeader';
import { TabPanel, Tabs } from '../ui/Tabs';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { LoadFailed } from '../ui/LoadState';
import { Spinner } from '../../ui/Spinner';
import { useAdmin } from '../AdminContext';
import { useCustomers } from './useCustomers';
import { attentionLabel, customerName, customerSearchText, initials, shapeLabel } from './customerRules';
import { btnPrimary, btnSecondary, inputCls, listBox, rowLink } from '../ui/styles';
import { useCan } from '../useCan';
import { NewCustomerDialog } from './NewCustomerDialog';
import { dayMedium, today } from '../../core/format/dates';
import { plural } from '../../core/format/text';
import { errorMessage } from '../../core/errors';
import { downloadCsv } from '../../utils/download';
import { exportCustomers } from '../adminApi';
import { useReauth } from '../useReauth';
import { customerCsv, customerExportFileName } from './customerExport';
import type { CustomerSummary } from '../../core/types/adminRequests';
import type { Course } from '../../core/types/account';
const filters = [{
  key: 'alle',
  label: 'Alle'
}, {
  key: 'aandacht',
  label: 'Vraagt aandacht'
}, {
  key: 'thuis',
  label: 'Webshop'
}, {
  key: 'salon',
  label: 'Salon'
}, {
  key: 'abonnement',
  label: 'Abonnement'
}, {
  key: 'kuur',
  label: 'Lopende kuur'
}] as const;
type FilterKey = (typeof filters)[number]['key'];
const matches: Record<FilterKey, (c: CustomerSummary) => boolean> = {
  alle: () => true,
  aandacht: c => c.attention.length > 0,
  thuis: c => c.shape.home,
  salon: c => c.shape.salon,
  abonnement: c => c.subscription === 'actief' || c.subscription === 'gepauzeerd',
  // Nog sessies te gaan; een afgeronde kuur telt niet (die staat onder "Vraagt aandacht").
  kuur: c => Boolean(c.course && c.course.done < c.course.total)
};

/** "Goddess Lift 2/6, vooruit betaald tot 3 okt 2028" — alleen in het kuurfilter, waar het de vraag is. */
const kuurLabel = (course: Course, name = course.treatmentSlug) => `${name} ${course.done}/${course.total}${course.prepaid ? `, vooruit betaald tot ${dayMedium(course.prepaid.validUntil)}` : ''}`;

/** De klanten die de lijst toont: filter en zoekterm, wie aandacht vraagt eerst. Ook voor de export. */
function visible(customers: readonly CustomerSummary[], filter: FilterKey, query: string) {
  const q = query.trim().toLowerCase();
  return customers.filter(matches[filter]).filter(c => !q || customerSearchText(c).includes(q)).sort((a, b) => b.attention.length - a.attention.length || a.firstName.localeCompare(b.firstName));
}

/** Zoveel meldingen passen naast de naam; de rest staat in het dossier. */
const BADGES_IN_ROW = 2;

/** Alle klanten: wie ze zijn, wat ze afnemen en waar Sarah iets mee moet. */
export function Customers() {
  const {
    customers,
    status,
    reload
  } = useCustomers();
  const {
    index
  } = useAdmin();
  const [params, setParams] = useSearchParams();
  const filter: FilterKey = filters.find(f => f.key === params.get('filter'))?.key ?? 'alle';
  // Zoekterm in het adres, net als het filter: na een dossier openen en Terug staat hij er nog.
  const query = params.get('zoek') ?? '';
  const setQuery = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set('zoek', value);else next.delete('zoek');
    setParams(next, {
      replace: true
    });
  };
  const list = useMemo(() => visible(customers, filter, query), [customers, filter, query]);
  const [exporting, setExporting] = useState(false);
  const [creating, setCreating] = useState(false);
  const {
    canAll
  } = useCan();
  const {
    guard,
    dialog
  } = useReauth('Het klantbestand exporteren vraagt een extra bevestiging.');

  /** Haalt de rijen vers op (de server vraagt een recente login) en downloadt wat de lijst nu toont. */
  const exportList = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await guard(() => exportCustomers());
      if (!res.done) return;
      const rows = visible(res.value, filter, query);
      const label = filter === 'alle' ? undefined : filters.find(f => f.key === filter)?.label;
      downloadCsv(customerExportFileName(today(), label), customerCsv(rows, slug => index.treatmentBySlug(slug)?.name));
      toast.success(`${plural(rows.length, 'klant', 'klanten')} geëxporteerd.`);
    } catch (err) {
      toast.error(errorMessage(err) ?? 'Exporteren lukte niet. Probeer het opnieuw.');
    } finally {
      setExporting(false);
    }
  };
  return <div data-id="element-1264" data-mp="1fg17smra6fb3">
      {dialog}
      {canAll('customers.edit') && <NewCustomerDialog open={creating} onClose={() => setCreating(false)} customers={customers} onCreated={reload} data-id="element-1265" data-mp="1bcwaqzpyz5tu" />}
      <PageHeader title="Klanten" description="Iedereen met een dossier. Hier vul je het huidplan in, houd je de kuur bij en bevestig je afspraken. Wat je aanpast, ziet de klant direct in Mijn omgeving." actions={<>
            <button type="button" onClick={exportList} disabled={exporting || list.length === 0} className={btnSecondary} data-id="element-1267" data-mp="3r1tehbg58w0p">
              {exporting ? <Spinner data-id="element-1268" data-mp="3vzd395agwr6w" /> : <DownloadIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1269" data-mp="3w04p3oe9jajz" />}
              {exporting ? 'Exporteren…' : 'Exporteren (CSV)'}
            </button>
            {canAll('customers.edit') && <button type="button" onClick={() => setCreating(true)} className={btnPrimary} data-id="element-1270" data-mp="339wf4jtvrwgh">
                <UserPlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1271" data-mp="389r24cajc1w9" />
                Nieuwe klant
              </button>}
          </>} data-id="element-1266" data-mp="16euoy715sseo" />

      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-1272" data-mp="2flvbnpbk767x">
        <Tabs id="klanten-filter" label="Filter" variant="pill" value={filter} onChange={key => setParams(key === 'alle' ? {} : {
        filter: key
      }, {
        replace: true
      })} tabs={filters.map(f => ({
        ...f,
        count: f.key === 'aandacht' ? customers.filter(matches.aandacht).length || undefined : undefined
      }))} data-id="element-1273" data-mp="2kk6idr6k50lx" />
        <label className="relative block lg:w-72" data-id="element-1274" data-mp="3smvctft765xs">
          <span className="sr-only" data-id="element-1275" data-mp="3sp83chd0tomg">Zoek een klant</span>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-1276" data-mp="34ykhsta6n0hf" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Naam, e-mail, telefoon of plaats" className={`${inputCls} pl-9`} data-id="element-1277" data-mp="300sp3x1prac6" />
        </label>
      </div>

      <TabPanel id="klanten-filter" value={filter} data-id="element-1278" data-mp="3918u5ay3upj5">
      {status === 'loading' && customers.length === 0 ? <div className="flex items-center gap-2 py-12 text-[14px] text-muted" role="status" data-id="element-1279" data-mp="t9tbnbuv4adn">
          <Spinner data-id="element-1280" data-mp="ob003oz4ytg4" /> Klanten laden…
        </div> : status === 'error' ? <LoadFailed icon={UsersIcon} title="De klanten konden niet laden" onRetry={reload} data-id="element-1281" data-mp="womgb75hr7p2" /> : list.length === 0 ? <EmptyState icon={UsersIcon} title={query ? `Niemand gevonden voor “${query}”` : 'Geen klanten in deze lijst'} action={<button type="button" onClick={() => setParams({}, {
        replace: true
      })} className={btnSecondary} data-id="element-1283" data-mp="rrmdr67uon2p">
              Filters wissen
            </button>} data-id="element-1282" data-mp="10p6fshdjened" /> : <ul className={listBox} data-id="element-1284" data-mp="15qlxq0kf4dsj">
          {list.map(c => <li key={c.id} data-id="element-1285" data-mp="15qc6yarju2ls">
              <Link to={`/be/klanten/${c.id}`} className={rowLink} data-id="element-1286" data-mp="1app53rnvew3o">
                <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sand font-display text-[15px] text-ink" data-id="element-1287" data-mp="1anujeb4ryzyj">
                  {initials(c)}
                </span>
                <span className="min-w-0 flex-1" data-id="element-1288" data-mp="2382r8tzugtmx">
                  <span className="flex flex-wrap items-center gap-2" data-id="element-1289" data-mp="34s7r6bjkcod">
                    <span className="truncate text-[15px] text-ink" data-id="element-1290" data-mp="3uio7tkfnisvu">{customerName(c)}</span>
                    {c.bookingBlocked && <Badge tone="neutral" data-id="element-1291" data-mp="3pitgxgvvm3as">Boekt niet online</Badge>}
                    {c.attention.slice(0, BADGES_IN_ROW).map(f => <Badge key={f} tone={f === 'aanvraag' ? 'accent' : 'soft'} data-id="element-1292" data-mp="3ljaswc8rdc3q">
                        {attentionLabel[f]}
                      </Badge>)}
                  </span>
                  <span className="mt-0.5 block truncate text-[13px] text-muted" data-id="element-1293" data-mp="h4b2mxfcdriq">
                    {[shapeLabel(c.shape), c.city, c.subscription === 'actief' && 'abonnement', c.subscription === 'gepauzeerd' && 'abonnement gepauzeerd', filter === 'kuur' && c.course && kuurLabel(c.course, index.treatmentBySlug(c.course.treatmentSlug)?.name)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className="hidden shrink-0 text-right text-[13px] sm:block" data-id="element-1294" data-mp="1dacs36njpi2a">
                  {c.nextAppointment ? <>
                      <span className="block text-ink" data-id="element-1295" data-mp="1m2y9aibj3gp0">
                        {dayMedium(c.nextAppointment.date)} · {c.nextAppointment.time}
                      </span>
                      <span className="block text-muted" data-id="element-1296" data-mp="1r3vruw0gacr1">{index.treatmentBySlug(c.nextAppointment.treatmentSlug)?.name}</span>
                    </> : c.lastVisit ? <span className="text-muted" data-id="element-1297" data-mp="20xkv1hxi4f15">Laatst geweest {dayMedium(c.lastVisit)}</span> : c.lastOrder ? <span className="text-muted" data-id="element-1298" data-mp="6lxuderxhkcu">Laatst besteld {dayMedium(c.lastOrder)}</span> : null}
                </span>
                <ChevronRightIcon className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-1299" data-mp="anrot5bj5s4k" />
              </Link>
            </li>)}
        </ul>}
      </TabPanel>
    </div>;
}